/**
 * Adaptateur de stockage hybride pour Supabase auth.
 *
 * Probleme empirique : Safari iPad Intelligent Tracking Prevention (ITP)
 * purge le `localStorage` apres 7 jours d'inactivite, ou des qu'il classe
 * le site comme "tracker" via ses heuristiques. Resultat : la session
 * Supabase disparait, l'utilisateur est deconnecte sans avertissement.
 *
 * Strategie : ecrire le token dans DEUX backends en parallele :
 *   - `localStorage` : sync, rapide, premier servi a la lecture
 *   - `IndexedDB` (via localforage) : asynchrone, **moins agressivement
 *     purge par ITP** (Apple considere IndexedDB comme du stockage
 *     d'application, pas du tracking)
 *
 * A la lecture :
 *   1. on essaie `localStorage`
 *   2. si ITP l'a purge (returns null), on lit IndexedDB
 *   3. si IndexedDB contient encore la session, on la re-ecrit dans
 *      `localStorage` pour les prochains acces synchrones et on la
 *      retourne -> reconnexion transparente
 *
 * A l'ecriture / suppression :
 *   on ecrit / supprime dans LES DEUX. La duplication est de l'ordre
 *   du kB par item (1 access_token + 1 refresh_token + 1 user JSON).
 *
 * Interface conforme au contrat Supabase :
 *   { getItem(k), setItem(k,v), removeItem(k) } avec returns Promise.
 */
import localforage from "localforage";

const KV = localforage.createInstance({
  name: "gsf-arbitral",
  storeName: "supabase_auth",
  description:
    "Cache Supabase auth tokens (anti Safari ITP). Cf. client/src/lib/supabaseStorage.ts.",
});

function _safeLocalGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    // localStorage refuse l'acces (mode private Safari, quota plein,
    // SecurityError suite a un cookies-blocked). On retombe sur IDB.
    return null;
  }
}

function _safeLocalSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* swallow — IDB prend le relais */
  }
}

function _safeLocalRemove(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* swallow */
  }
}

export const hybridSupabaseStorage = {
  async getItem(key: string): Promise<string | null> {
    if (typeof window === "undefined") return null;
    // 1) localStorage (sync, instantane)
    const fromLocal = _safeLocalGet(key);
    if (fromLocal !== null) return fromLocal;

    // 2) IndexedDB (fallback, asynchrone)
    try {
      const fromIdb = await KV.getItem<string>(key);
      if (fromIdb !== null) {
        // Rechauffe le cache rapide pour les prochains acces sync —
        // ITP a peut-etre purge le localStorage mais on lui re-injecte
        // une copie via cette ecriture, l'experience reste transparente.
        _safeLocalSet(key, fromIdb);
        return fromIdb;
      }
    } catch {
      // IndexedDB peut etre indisponible (mode private tres restrictif,
      // navigateur ancien). On laisse Supabase considerer la session
      // perdue -> il declenchera SIGNED_OUT, ProtectedRoute redirige.
    }
    return null;
  },

  async setItem(key: string, value: string): Promise<void> {
    if (typeof window === "undefined") return;
    _safeLocalSet(key, value);
    try {
      await KV.setItem(key, value);
    } catch {
      /* swallow — localStorage couvre */
    }
  },

  async removeItem(key: string): Promise<void> {
    if (typeof window === "undefined") return;
    _safeLocalRemove(key);
    try {
      await KV.removeItem(key);
    } catch {
      /* swallow */
    }
  },
};
